import { useState } from "react";
import { api, type ItineraryEvent } from "../lib/api";
import { Notice } from "./Layout";

const fmtDay = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : null;

const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : null;

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-1 text-sm text-ink/80">{value}</dd>
    </div>
  );
}

export function EventCard({
  event,
  partyMax,
  onSaved,
}: {
  event: ItineraryEvent;
  partyMax: number;
  onSaved: () => void;
}) {
  const [attending, setAttending] = useState<boolean | null>(event.rsvp?.attending ?? null);
  const [partySize, setPartySize] = useState(event.rsvp?.partySize || 1);
  const [dietary, setDietary] = useState(event.rsvp?.dietary ?? "");
  const [notes, setNotes] = useState(event.rsvp?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const answered = event.rsvp !== null;
  const timeRange = [fmtTime(event.startsAt), fmtTime(event.endsAt)].filter(Boolean).join(" – ");

  async function save(next: boolean) {
    setAttending(next);
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/rsvp", {
        eventId: event.id,
        attending: next,
        partySize: next ? partySize : 0,
        dietary,
        notes,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2600);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save that.");
      setAttending(event.rsvp?.attending ?? null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="card overflow-hidden">
      <div className="border-b border-line px-6 py-6 sm:px-8">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <h3 className="font-display text-3xl font-light">{event.name}</h3>
          {answered && (
            <span
              className={`rounded-full px-3 py-1 text-xs tracking-wide ${
                attending ? "bg-sage-soft text-sage" : "bg-[#f3ede6] text-muted"
              }`}
            >
              {attending ? `Going${partySize > 1 ? ` · ${partySize} of you` : ""}` : "Not going"}
            </span>
          )}
        </div>
        <p className="mt-2 text-muted">
          {fmtDay(event.startsAt)}
          {timeRange && ` · ${timeRange}`}
        </p>
        {event.description && (
          <p className="mt-4 leading-relaxed text-ink/80">{event.description}</p>
        )}

        <dl className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-3">
          <Detail label="Where" value={event.venueName} />
          <Detail label="Address" value={event.address} />
          <Detail label="Dress" value={event.dressCode} />
        </dl>

        {event.mapUrl && (
          <a
            href={event.mapUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-block text-sm text-sage underline decoration-line underline-offset-4"
          >
            Open in maps →
          </a>
        )}
      </div>

      <div className="bg-cream/60 px-6 py-6 sm:px-8">
        <p className="eyebrow mb-3">Will you be there?</p>

        <div className="flex gap-3">
          <button
            className={`btn flex-1 ${attending === true ? "btn-primary" : "btn-ghost"}`}
            disabled={busy}
            onClick={() => save(true)}
          >
            Joyfully accepts
          </button>
          <button
            className={`btn flex-1 ${attending === false ? "btn-primary" : "btn-ghost"}`}
            disabled={busy}
            onClick={() => save(false)}
          >
            Regretfully declines
          </button>
        </div>

        {attending === true && (
          <div className="mt-5 space-y-4">
            {partyMax > 1 && (
              <div>
                <label className="eyebrow mb-2 block" htmlFor={`party-${event.id}`}>
                  How many of you?
                </label>
                <select
                  id={`party-${event.id}`}
                  className="field"
                  value={partySize}
                  onChange={(e) => setPartySize(Number(e.target.value))}
                >
                  {Array.from({ length: partyMax }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? "person" : "people"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="eyebrow mb-2 block" htmlFor={`diet-${event.id}`}>
                Dietary needs
              </label>
              <input
                id={`diet-${event.id}`}
                className="field"
                value={dietary}
                onChange={(e) => setDietary(e.target.value)}
                placeholder="Vegetarian, nut allergy…"
              />
            </div>

            <div>
              <label className="eyebrow mb-2 block" htmlFor={`notes-${event.id}`}>
                Anything else?
              </label>
              <textarea
                id={`notes-${event.id}`}
                className="field min-h-20 resize-y"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Arriving late, bringing a song request…"
              />
            </div>

            <button className="btn btn-ghost" disabled={busy} onClick={() => save(true)}>
              {busy ? "Saving…" : "Save details"}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4">
            <Notice tone="error">{error}</Notice>
          </div>
        )}
        {saved && !error && (
          <p className="mt-4 text-sm text-sage">Saved — thank you!</p>
        )}
      </div>
    </article>
  );
}
